// staleTime dopasowany do harmonogramu odświeżania materialized views.
//
// Źródło planu porannego: deploy/maintenance-d1-plan.json.
// Framework wykonania: deploy/setup-mv-cron-framework.sql.
// (pg_cron, baza produkcyjna chodzi w GMT/UTC — godziny w specach poniżej są UTC).
// Każdy MV odświeża się o stałej minucie godziny (hourly) albo w stałych
// godzinach UTC (co N godzin / raz dziennie). Zamiast płaskiego staleTime
// cache czeka do najbliższej granicy "po odświeżeniu rodziny MV
// + bufor na runtime"; minuty w presetach uwzględniają już ten bufor.
//
// Minuta godziny jest niezależna od strefy czasowej (PL = pełne godziny
// przesunięcia), więc presety hourly działają identycznie latem i zimą.

export type MvBoundarySpec =
	| { kind: "hourly"; freshAtMinute: number }
	| { kind: "everyNHours"; n: number; freshAtMinute: number }
	| { kind: "dailyUtc"; freshAtHour: number; freshAtMinute: number }
	/** Stałe godziny UTC nie tworzące równego rastra co N h (np. 03:15/21:15). */
	| { kind: "dailyTimesUtc"; freshAtHoursUtc: readonly number[]; freshAtMinute: number };

const HOUR_MS = 60 * 60_000;
const MINUTE_MS = 60_000;

/** Epoch ms pierwszej granicy świeżości MV ściśle po `fromMs`. */
export function nextMvBoundary(spec: MvBoundarySpec, fromMs: number): number {
	const from = new Date(fromMs);
	switch (spec.kind) {
		case "hourly": {
			const candidate = Date.UTC(
				from.getUTCFullYear(),
				from.getUTCMonth(),
				from.getUTCDate(),
				from.getUTCHours(),
				spec.freshAtMinute,
			);
			return candidate > fromMs ? candidate : candidate + HOUR_MS;
		}
		case "everyNHours": {
			const refreshHour = from.getUTCHours() - (from.getUTCHours() % spec.n);
			let candidate = Date.UTC(
				from.getUTCFullYear(),
				from.getUTCMonth(),
				from.getUTCDate(),
				refreshHour,
				spec.freshAtMinute,
			);
			while (candidate <= fromMs) {
				candidate += spec.n * HOUR_MS;
			}
			return candidate;
		}
		case "dailyUtc": {
			const candidate = Date.UTC(
				from.getUTCFullYear(),
				from.getUTCMonth(),
				from.getUTCDate(),
				spec.freshAtHour,
				spec.freshAtMinute,
			);
			return candidate > fromMs ? candidate : candidate + 24 * HOUR_MS;
		}
		case "dailyTimesUtc": {
			const todays = spec.freshAtHoursUtc.map((h) =>
				Date.UTC(
					from.getUTCFullYear(),
					from.getUTCMonth(),
					from.getUTCDate(),
					h,
					spec.freshAtMinute,
				),
			);
			const next = todays.filter((t) => t > fromMs).sort((a, b) => a - b)[0];
			if (next !== undefined) return next;
			return Math.min(...todays) + 24 * HOUR_MS;
		}
	}
}

/** Epoch ms ostatniej granicy świeżości MV nie późniejszej niż `fromMs`. */
export function prevMvBoundary(spec: MvBoundarySpec, fromMs: number): number {
	// Odstęp między granicami to max 24 h, więc pierwsza granica po
	// (fromMs − 25 h) jest na pewno <= fromMs; stamtąd idziemy do przodu.
	let boundary = nextMvBoundary(spec, fromMs - 25 * HOUR_MS);
	for (;;) {
		const following = nextMvBoundary(spec, boundary);
		if (following > fromMs) return boundary;
		boundary = following;
	}
}

/**
 * Funkcyjny staleTime dla TanStack Query v5: dane pobrane o dataUpdatedAt
 * pozostają świeże do najbliższej granicy odświeżenia MV. Refetch następuje
 * dopiero gdy backend faktycznie może mieć nowe dane.
 */
export function mvStaleTime(
	spec: MvBoundarySpec,
): (query: { state: { dataUpdatedAt: number } }) => number {
	return (query) => {
		const fetchedAt = query.state.dataUpdatedAt || Date.now();
		return Math.max(MINUTE_MS, nextMvBoundary(spec, fetchedAt) - fetchedAt);
	};
}

/**
 * staleTime dla odpowiedzi hybrydowych (kilka rodzin MV): dane są świeże do
 * NAJBLIŻSZEJ granicy którejkolwiek rodziny — refetch, gdy dowolne źródło
 * mogło się odświeżyć.
 */
export function mvStaleTimeMulti(
	specs: readonly MvBoundarySpec[],
): (query: { state: { dataUpdatedAt: number } }) => number {
	return (query) => {
		const fetchedAt = query.state.dataUpdatedAt || Date.now();
		const next = Math.min(...specs.map((s) => nextMvBoundary(s, fetchedAt)));
		return Math.max(MINUTE_MS, next - fetchedAt);
	};
}

/**
 * staleTime dla odpowiedzi hybrydowych MV + komponent LIVE (np. ceny:
 * dzienny MV sprzedaży + żywe tabele cenników): dane są świeże do granicy
 * MV, ale nigdy dłużej niż `liveTtlMs` — komponent live zmienia się między
 * granicami, a bounded TTL nie przywraca refetch stormu (endpointy czysto
 * MV zostają na samych granicach).
 */
export function mvStaleTimeWithLiveTtl(
	spec: MvBoundarySpec,
	liveTtlMs: number,
): (query: { state: { dataUpdatedAt: number } }) => number {
	return (query) => {
		const fetchedAt = query.state.dataUpdatedAt || Date.now();
		const toBoundary = nextMvBoundary(spec, fetchedAt) - fetchedAt;
		return Math.max(MINUTE_MS, Math.min(toBoundary, liveTtlMs));
	};
}

/**
 * Ostatni KOMPLETNY dzień kalendarzowy pokryty przez rodzinę MV wg jej
 * harmonogramu: refresh z granicy w dniu R (UTC) domyka dni <= R−1.
 * Lustrzana logika backendowego `mv_source_data_as_of` (dataAsOf w meta).
 */
export function lastCompleteDayUtc(spec: MvBoundarySpec, nowMs: number): Date {
	const boundary = new Date(prevMvBoundary(spec, nowMs));
	const boundaryDayStart = Date.UTC(
		boundary.getUTCFullYear(),
		boundary.getUTCMonth(),
		boundary.getUTCDate(),
	);
	return new Date(boundaryDayStart - 24 * HOUR_MS);
}

/**
 * Plan poranny kończy wszystkie budżety o 04:55 UTC; cache sprawdza nowe
 * odpowiedzi od 04:56 (06:56 latem / 05:56 zimą w Warszawie).
 * To termin sprawdzenia, nie dowód udanego refreshu: o świeżości decyduje
 * historia sukcesów zwracana przez backend.
 * Zgodność z deploy/maintenance-d1-plan.json sprawdza maintenance-cache.test.ts.
 */
const MORNING_REPORTS = { kind: "dailyUtc", freshAtHour: 4, freshAtMinute: 56 } as const;

export const MV_STALE = {
	/** Dzisiejsza sprzedaż: lekki agregat :05/:07, bufor do :10. */
	dailySalesCurrent: { kind: "hourly", freshAtMinute: 10 },
	stock: MORNING_REPORTS,
	sales90d: MORNING_REPORTS,
	productReport: MORNING_REPORTS,
	dailySales: MORNING_REPORTS,
	// Historyczne nazwy zachowane dla istniejących wywołań; kadencja jest dobowa.
	purchaseAnalysis3h: MORNING_REPORTS,
	marginCalc6h: MORNING_REPORTS,
	strategHistoricalDaily: MORNING_REPORTS,
	dashboardSalesDaily: MORNING_REPORTS,
	dashboardPurchasesDaily: MORNING_REPORTS,
	inventoryDailySnapshot: MORNING_REPORTS,
	sprzedazZrealizowana: MORNING_REPORTS,
} as const satisfies Record<string, MvBoundarySpec>;

export const PRODUCT_REPORT_PAGE_STALE = [
	MORNING_REPORTS,
] as const satisfies readonly MvBoundarySpec[];
